import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import tokenManager from '../utils/tokenManager';
import sessionManager from '../utils/sessionManager';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);

  const validateSession = async () => {
    const token = tokenManager.getAccessToken();
    
    if (!token) {
      setUser(null);
      setEntities([]);
      setSelectedEntity(null);
      setSessionValid(false);
      setLoading(false);
      return false;
    }

    // PROMPT-046: Check session manager validity first
    if (!sessionManager.isSessionValid()) {
      console.log('Session manager reports session expired');
      tokenManager.clearTokens();
      localStorage.removeItem('user');
      localStorage.removeItem('entities');
      localStorage.removeItem('selectedEntity');
      setUser(null);
      setEntities([]);
      setSelectedEntity(null);
      setSessionValid(false);
      setLoading(false);
      return false;
    }

    try {
      const response = await authAPI.getProfile();
      const profileUser = response.data;
      
      setUser(profileUser);
      localStorage.setItem('user', JSON.stringify(profileUser));
      setSessionValid(true);
      
      return true;
    } catch (error) {
      const status = error.response?.status;
      
      if (status === 401 || status === 403) {
        console.error('Session expired or unauthorized:', error);
        tokenManager.clearTokens();
        localStorage.removeItem('user');
        localStorage.removeItem('entities');
        localStorage.removeItem('selectedEntity');
        localStorage.removeItem('lastActivity');
        setUser(null);
        setEntities([]);
        setSelectedEntity(null);
        setSessionValid(false);
        return false;
      } else {
        console.warn('Profile API error (non-auth):', status, error.message);
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
          setSessionValid(true);
          return true;
        }
        return false;
      }
    }
  };

  useEffect(() => {
    const initSession = async () => {
      setLoading(true);
      const token = tokenManager.getAccessToken();
      const savedUser = localStorage.getItem('user');
      const savedEntities = localStorage.getItem('entities');
      const savedSelectedEntity = localStorage.getItem('selectedEntity');
      
      if (token && savedUser) {
        try {
          setUser(JSON.parse(savedUser || 'null'));
        } catch (e) {
          console.error('Error parsing saved user:', e);
          localStorage.removeItem('user');
        }
        setSessionValid(true);
        
        if (savedEntities) {
          try {
            setEntities(JSON.parse(savedEntities || '[]'));
          } catch (e) {
            console.error('Error parsing saved entities:', e);
            localStorage.removeItem('entities');
          }
        }
        if (savedSelectedEntity) {
          try {
            setSelectedEntity(JSON.parse(savedSelectedEntity || 'null'));
          } catch (e) {
            console.error('Error parsing saved selected entity:', e);
            localStorage.removeItem('selectedEntity');
          }
        }
        
        // PROMPT-046: Initialize session manager for existing session
        sessionManager.resetTimers();
        
        validateSession().catch(err => {
          console.warn('Background session validation failed:', err);
        });
      }
      
      setLoading(false);
    };

    initSession();
  }, []);

  const login = async (username, password) => {
    try {
      console.log('Attempting login with:', username);
      const response = await authAPI.login(username, password);
      console.log('Login response:', response);

      // Check if OTP is required
      if (response.data?.otp_required) {
        return {
          success: false,
          otp_required: true,
          mobile_selection: response.data.mobile_selection || false,
          mobiles: response.data.mobiles || [],
          otp_token: response.data.otp_token,
          mobile: response.data.mobile,
          message: response.data.message,
        };
      }
      
      // Backend returns: { token, user, entities }
      const { token, user, entities: userEntities } = response.data;
      
      if (!token || !user) {
        console.error('Invalid response structure:', response.data);
        return { success: false, error: 'Invalid response from server' };
      }
      
      // Set token with 24 hour expiry
      tokenManager.setTokens(token, null, 86400);
      
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setSessionValid(true);
      
      // PROMPT-046: Initialize session manager for 30-minute timeout
      sessionManager.resetTimers();
      
      if (userEntities && userEntities.length > 0) {
        localStorage.setItem('entities', JSON.stringify(userEntities));
        setEntities(userEntities);
        if (userEntities.length === 1) {
          setSelectedEntity(userEntities[0]);
          localStorage.setItem('selectedEntity', JSON.stringify(userEntities[0]));
        }
      }
      
      return { success: true, entities: userEntities || [] };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const verifyOTP = async (otpToken, otp) => {
    try {
      const response = await authAPI.verifyOTP(otpToken, otp);
      const { token, user, entities: userEntities } = response.data;

      if (!token || !user) {
        return { success: false, error: 'Invalid response from server' };
      }

      tokenManager.setTokens(token, null, 86400);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setSessionValid(true);
      sessionManager.resetTimers();

      if (userEntities && userEntities.length > 0) {
        localStorage.setItem('entities', JSON.stringify(userEntities));
        setEntities(userEntities);
        if (userEntities.length === 1) {
          setSelectedEntity(userEntities[0]);
          localStorage.setItem('selectedEntity', JSON.stringify(userEntities[0]));
        }
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'OTP verification failed' };
    }
  };

  const resendOTP = async (otpToken) => {
    try {
      const response = await authAPI.resendOTP(otpToken);
      return { success: true, mobile: response.data?.mobile };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to resend OTP' };
    }
  };

  const selectMobile = async (otpToken, mobile) => {
    try {
      const response = await authAPI.selectMobile(otpToken, mobile);
      return {
        success: true,
        otp_token: response.data?.otp_token,
        mobile: response.data?.mobile,
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to send OTP' };
    }
  };

  const logout = async () => {
    // PROMPT-046: Secure Logout Implementation with session cleanup
    try {
      // Get tokens before clearing
      const accessToken = tokenManager.getAccessToken();
      const refreshToken = tokenManager.getRefreshToken();
      
      // Call logout API to blacklist tokens and cleanup server-side
      if (accessToken || refreshToken) {
        await authAPI.logout({ 
          access_token: accessToken, 
          refresh_token: refreshToken 
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with cleanup even if API fails (idempotent)
    } finally {
      // STEP 6: Frontend Storage Cleanup
      
      // Clear tokens
      tokenManager.clearTokens();
      
      // Clear localStorage completely
      localStorage.removeItem('user');
      localStorage.removeItem('entities');
      localStorage.removeItem('selectedEntity');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('lastActivity');
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear in-memory state
      setUser(null);
      setEntities([]);
      setSelectedEntity(null);
      setSessionValid(false);
      
      // STEP 7: Cancel Active Timers
      // Stop token refresh timer if exists
      if (window.tokenRefreshTimer) {
        clearInterval(window.tokenRefreshTimer);
        window.tokenRefreshTimer = null;
      }
      
      // Stop any polling timers
      if (window.pollingTimers) {
        window.pollingTimers.forEach(timer => clearInterval(timer));
        window.pollingTimers = [];
      }
      
      // PROMPT-046: Cleanup session manager
      sessionManager.cleanup();
      
      console.log('Logout completed: All storage, state, timers, and session manager cleared');
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const selectEntity = (entity) => {
    setSelectedEntity(entity);
    localStorage.setItem('selectedEntity', JSON.stringify(entity));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      entities, 
      selectedEntity, 
      loading,
      sessionValid,
      login, 
      verifyOTP,
      resendOTP,
      selectMobile,
      logout, 
      updateUser, 
      selectEntity,
      validateSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
